import { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout';
import api from '../../../api/axios';
import useAuthStore from '../../../store/authStore';
import { todayISO } from '../../../utils/dateFormat';
import SearchSelect from '../../../components/SearchSelect';
import { confirm } from '../../../components/ConfirmDialog';

const fmt = (v) => Number(v || 0).toFixed(2);
const today = todayISO;

export default function Staff() {
    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

    // ── State ──────────────────────────────────────────────────────
    const [payroll,   setPayroll]   = useState([]);   // birləşmiş işçi+ofisiant
    const [staffTab,  setStaffTab]  = useState('all'); // filtr: all | waiter | staff
    const [editWaiter, setEditWaiter] = useState(null); // ofisiant redaktə popup
    const [wName, setWName] = useState('');
    const [wPin, setWPin]   = useState('');
    const [wSaving, setWSaving] = useState(false);
    const [positions, setPositions] = useState([]);
    const [loading,   setLoading]   = useState(true);

    // Sol panel — seçim və ödəniş
    const [selectedKey, setSelectedKey] = useState('');
    const [payAmount,   setPayAmount]   = useState('');
    const [payNote,     setPayNote]     = useState('');
    const [paying,      setPaying]      = useState(false);

    // Staff/position form modalları
    const [showStaffForm, setShowStaffForm] = useState(false);
    const [showPosForm,   setShowPosForm]   = useState(false);
    const [editStaff,     setEditStaff]     = useState(null);
    const [staffForm,     setStaffForm]     = useState({ name: '', position_id: '', salary: '', start_date: today(), carried_over: '', prepaid_this: '' });
    const [posName,       setPosName]       = useState('');
    const [saving,        setSaving]        = useState(false);

    // Bu ayın maaş ödənişləri (refund üçün)
    const [recentPays,  setRecentPays]  = useState([]);
    const [refundingId, setRefundingId] = useState(null);

    // Cərimə
    const [showFine,  setShowFine]  = useState(false);
    const [fineAmount, setFineAmount] = useState('');
    const [fineNote,  setFineNote]  = useState('');
    const [fining,    setFining]    = useState(false);

    // ── Load ───────────────────────────────────────────────────────
    const load = async () => {
        setLoading(true);
        try {
            const [pRes, posRes, payRes] = await Promise.all([
                api.get('/staff/payroll'),
                api.get('/staff/positions').catch(() => ({ data: { data: [] } })),
                api.get('/staff/payments/recent').catch(() => ({ data: { data: [] } })),
            ]);
            setPayroll(pRes.data.data || []);
            setPositions(posRes.data.data || []);
            setRecentPays(payRes.data.data || []);
        } catch {
            toast.error('Yüklənmədi.');
        } finally {
            setLoading(false);
        }
    };

    const refundPay = async (p) => {
        if (!(await confirm(`${p.staff_name} — ${Number(p.amount).toFixed(2)} ₼ ödənişi refund edilsin?\nMəbləğ qalığa qayıdacaq.`))) return;
        setRefundingId(p.id);
        try {
            const res = await api.post(`/staff/payments/${p.id}/refund`);
            toast.success(res.data?.msg || 'Refund edildi.');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Refund alınmadı.');
        } finally {
            setRefundingId(null);
        }
    };

    useEffect(() => { load(); }, []);

    const selected = useMemo(
        () => payroll.find(p => p.key === selectedKey) || null,
        [payroll, selectedKey]
    );

    // Seçim dəyişəndə ödəniş məbləği BOŞ (0) gəlsin — qalıq avtomatik yazılmasın
    useEffect(() => {
        setPayAmount('');
    }, [selectedKey]); // eslint-disable-line

    // ── Ödəniş ─────────────────────────────────────────────────────
    const doPay = async () => {
        if (!selected) return toast.error('İşçi seçin.');
        const amt = Number(payAmount);
        if (!amt || amt <= 0) return toast.error('Məbləğ daxil edin.');

        const now = new Date();
        const body = { amount: amt, note: payNote || null, year: now.getFullYear(), month: now.getMonth() + 1 };
        setPaying(true);
        try {
            if (selected.type === 'waiter') {
                await api.post(`/auth/waiter-pay/${selected.id}`, body);
            } else {
                await api.post(`/staff/${selected.id}/pay`, body);
            }
            toast.success(`${selected.name} — ${fmt(amt)} ₼ ödənildi.`);
            setPayAmount(''); setPayNote('');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Ödəniş alınmadı.');
        } finally {
            setPaying(false);
        }
    };

    // ── Cərimə ─────────────────────────────────────────────────────
    const doFine = async () => {
        if (!selected) return toast.error('İşçi seçin.');
        const amt = Number(fineAmount);
        if (!amt || amt <= 0) return toast.error('Cərimə məbləği daxil edin.');
        setFining(true);
        try {
            await api.post('/staff/fine', {
                target_type: selected.type, target_id: selected.id,
                amount: amt, note: fineNote || null,
            });
            toast.success(`${selected.name} — ${fmt(amt)} ₼ cərimə yazıldı.`);
            setShowFine(false); setFineAmount(''); setFineNote('');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Cərimə alınmadı.');
        } finally {
            setFining(false);
        }
    };

    // ── Staff CRUD ─────────────────────────────────────────────────
    const openAddStaff = () => {
        setEditStaff(null);
        setStaffForm({ name: '', position_id: '', salary: '', start_date: today(), carried_over: '', prepaid_this: '', salary_type: 'fixed', salary_percent: '', accrual_mode: 'monthly', salary_min: '' });
        setShowStaffForm(true);
    };
    const saveStaff = async () => {
        if (!staffForm.name.trim() || !staffForm.start_date)
            return toast.error('Ad və başlama tarixi tələb olunur.');
        if (staffForm.salary_type === 'percent') {
            if (!(Number(staffForm.salary_percent) > 0)) return toast.error('Faiz 0-dan böyük olmalıdır.');
        } else if (!(Number(staffForm.salary) > 0)) {
            return toast.error('Maaş 0-dan böyük olmalıdır.');
        }
        setSaving(true);
        try {
            if (editStaff) await api.put(`/staff/${editStaff.id}`, staffForm);
            else           await api.post('/staff', staffForm);
            toast.success(editStaff ? 'Yeniləndi.' : 'İşçi əlavə edildi.');
            setShowStaffForm(false); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setSaving(false); }
    };

    // İşçi redaktə — tam record-u çək (position_id, start_date lazımdır), formu doldur
    const openEditStaff = async (p) => {
        try {
            const res = await api.get('/staff');
            const s = (res.data?.data || []).find(x => Number(x.id) === Number(p.id));
            if (!s) return toast.error('İşçi tapılmadı.');
            setEditStaff(s);
            setStaffForm({
                name: s.name || '',
                position_id: s.position_id || '',
                salary: s.salary ?? '',
                start_date: (s.start_date || '').toString().slice(0, 10) || today(),
                carried_over: s.current_month?.carried_over ?? 0,
                prepaid_this: s.current_month?.prepaid ?? '',
                salary_type: s.salary_type === 'percent' ? 'percent' : 'fixed',
                salary_percent: s.salary_percent ?? '',
                accrual_mode: s.accrual_mode === 'live' ? 'live' : 'monthly',
                salary_min: Number(s.salary_min) > 0 ? s.salary_min : '',
            });
            setShowStaffForm(true);
        } catch { toast.error('Yüklənmədi.'); }
    };

    const deleteStaff = async (p) => {
        if (!(await confirm(`${p.name} işçisini silmək istəyirsiniz?\n(Maaş tarixçəsi qalır, siyahıdan çıxır)`))) return;
        try {
            await api.delete(`/staff/${p.id}`);
            toast.success('İşçi silindi.');
            if (selectedKey === p.key) setSelectedKey(null);
            load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Silinmədi.'); }
    };

    // Ofisiant redaktə popup — ad + PIN
    const openEditWaiter = (p) => { setEditWaiter(p); setWName(p.name || ''); setWPin(p.pin || ''); };
    const saveWaiterEdit = async (e) => {
        e.preventDefault();
        const nm = String(wName || '').trim();
        if (nm.length < 2) return toast.error('Ad ən az 2 simvol olmalıdır.');
        const pinDiffers = String(wPin) !== String(editWaiter?.pin || '');
        if (pinDiffers && !/^\d{4}$/.test(wPin)) return toast.error('PIN tam 4 rəqəm olmalıdır.');
        setWSaving(true);
        try {
            if (nm !== String(editWaiter?.name || '')) await api.put(`/auth/users/${editWaiter.id}/name`, { name: nm });
            if (pinDiffers) await api.put(`/auth/users/${editWaiter.id}/pin`, { pin: wPin });
            toast.success('Yadda saxlanıldı.');
            setEditWaiter(null); setWName(''); setWPin('');
            load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setWSaving(false); }
    };

    // Ofisiant (pos_users) sil — POS girişini də silir
    const deleteWaiter = async (p) => {
        if (!(await confirm(`${p.name} ofisiantını silmək istəyirsiniz?\n(POS girişi də silinir; ödəniş tarixçəsi qalır)`))) return;
        try {
            await api.delete(`/auth/users/${p.id}`);
            toast.success('Ofisiant silindi.');
            if (selectedKey === p.key) setSelectedKey(null);
            load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Silinmədi.'); }
    };

    const savePos = async () => {
        if (!posName.trim()) return toast.error('Vəzifə adı daxil edin.');
        setSaving(true);
        try {
            await api.post('/staff/positions', { name: posName.trim() });
            toast.success('Vəzifə əlavə edildi.'); setPosName(''); setShowPosForm(false); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setSaving(false); }
    };

    // ── Render ─────────────────────────────────────────────────────
    return (
        <Layout>
            <div className="max-w-6xl mx-auto p-4">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                    <h1 className="text-2xl font-black text-slate-800">👥 İşçilər</h1>
                    {isAdmin && (
                        <div className="flex gap-2">
                            <button onClick={() => setShowPosForm(true)}
                                className="px-4 py-2 rounded-xl text-sm font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all">
                                + Vəzifə
                            </button>
                            <button onClick={openAddStaff}
                                className="px-4 py-2 rounded-xl text-sm font-bold bg-primary-600 text-white hover:bg-primary-700 transition-all">
                                + İşçi əlavə et
                            </button>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-48 text-slate-400">Yüklənir...</div>
                ) : (
                    <div className="grid lg:grid-cols-5 gap-5 min-w-0">
                        {/* ── SOL: Ödəniş paneli ─────────────────────────── */}
                        <div className="lg:col-span-2 min-w-0">
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sticky top-4">
                                <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">💵 Maaş / Qazanc ödə</h2>

                                {/* Modern combobox — search & select */}
                                <label className="text-[11px] font-bold text-slate-500 uppercase mb-1.5 block">İşçi seçin</label>
                                <div className="mb-4">
                                    <SearchSelect
                                        value={selectedKey}
                                        onChange={(v) => setSelectedKey(v)}
                                        placeholder="🔍 İşçi axtar və seç..."
                                        options={payroll.map(p => ({
                                            value: p.key,
                                            label: `${p.type === 'waiter' ? '🧑‍🍳' : '👤'} ${p.position} · ${p.name}${p.remaining > 0 ? ` — qalıq ${fmt(p.remaining)} ₼` : ''}`,
                                        }))}
                                    />
                                </div>

                                {selected ? (
                                    <>
                                        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 mb-4 space-y-1.5 text-sm">
                                            <div className="flex justify-between"><span className="text-slate-500">Ad</span><span className="font-bold text-slate-800">{selected.name}</span></div>
                                            <div className="flex justify-between"><span className="text-slate-500">Vəzifə</span><span className="font-semibold text-slate-700">{selected.position}</span></div>
                                            <div className="flex justify-between"><span className="text-slate-500">Ödəniş növü</span><span className="font-semibold text-slate-700">{selected.pay_label}</span></div>
                                            <div className="border-t border-slate-200 my-1.5"></div>
                                            <div className="flex justify-between"><span className="text-slate-500">Maaş / Qazanc</span><span className="font-bold text-slate-800">{fmt(selected.earned)} ₼</span></div>
                                            <div className="flex justify-between"><span className="text-slate-500">Götürüb</span><span className="font-bold text-green-600">{fmt(selected.paid)} ₼</span></div>
                                            <div className="flex justify-between"><span className="text-slate-500">Cərimə</span><span className={`font-semibold ${Number(selected.fine) > 0 ? 'text-rose-600' : 'text-slate-500'}`}>{fmt(selected.fine || 0)} ₼</span></div>
                                            <div className="flex justify-between text-base">
                                                <span className="font-bold text-slate-600">Qalıq</span>
                                                <span className={`font-black ${selected.remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(selected.remaining)} ₼</span>
                                            </div>
                                        </div>

                                        <label className="text-[11px] font-bold text-slate-500 uppercase mb-1.5 block">Ödəniş məbləği (₼)</label>
                                        <input
                                            type="number" step="0.01" min="0"
                                            value={payAmount}
                                            placeholder="0"
                                            onChange={(e) => setPayAmount(e.target.value)}
                                            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-lg font-bold font-mono focus:outline-none focus:border-primary-400 mb-3"
                                        />
                                        <input
                                            type="text"
                                            value={payNote}
                                            onChange={(e) => setPayNote(e.target.value)}
                                            placeholder="Qeyd (ixtiyari)"
                                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-primary-400 mb-4"
                                        />
                                        <button
                                            onClick={doPay}
                                            disabled={paying}
                                            className="w-full py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold disabled:opacity-50 transition-all"
                                        >
                                            {paying ? 'Saxlanır...' : '💵 Daxil et (ödə)'}
                                        </button>
                                        <button
                                            onClick={() => { setFineAmount(''); setFineNote(''); setShowFine(true); }}
                                            disabled={!(Number(selected.remaining) > 0)}
                                            className="mt-2 w-full py-2.5 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 font-bold hover:bg-rose-100 disabled:opacity-50 transition-all"
                                        >
                                            ⚖️ Cərimə yaz
                                        </button>
                                    </>
                                ) : (
                                    <p className="text-center text-sm text-slate-400 py-8">Ödəniş üçün soldan işçi seçin</p>
                                )}
                            </div>
                        </div>

                        {/* ── SAĞ: Cədvəl (yalnız göstərmə) ──────────────── */}
                        <div className="lg:col-span-3 min-w-0">
                            {/* Filtr: Hamısı / Ofisiant / İşçi */}
                            <div className="mb-3 inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                                {[
                                    { k: 'all',    lbl: 'Hamısı',      cnt: payroll.length },
                                    { k: 'waiter', lbl: '🧑‍🍳 Ofisiant', cnt: payroll.filter(p => p.type === 'waiter').length },
                                    { k: 'staff',  lbl: '👤 İşçi',       cnt: payroll.filter(p => p.type === 'staff').length },
                                ].map(({ k, lbl, cnt }) => (
                                    <button key={k} onClick={() => setStaffTab(k)}
                                        className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                                            staffTab === k ? 'bg-primary-500 text-white' : 'text-slate-500 hover:bg-slate-50'
                                        }`}>
                                        {lbl} <span className={`text-[11px] ${staffTab === k ? 'text-white/80' : 'text-slate-400'}`}>({cnt})</span>
                                    </button>
                                ))}
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[600px]">
                                        <thead>
                                            <tr className="bg-slate-50 border-b border-slate-100">
                                                <th className="px-3 py-3 text-left text-[11px] font-bold text-slate-400 uppercase">Ad</th>
                                                <th className="px-3 py-3 text-left text-[11px] font-bold text-slate-400 uppercase">Vəzifə</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-slate-400 uppercase">Qazanc</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-slate-400 uppercase">Ödənilib</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-slate-400 uppercase">Qalıq</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-slate-400 uppercase">Son ödəniş</th>
                                                {isAdmin && <th className="px-3 py-3 text-right text-[11px] font-bold text-slate-400 uppercase">Əməliyyat</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payroll
                                                .filter(p => staffTab === 'all' || p.type === staffTab)
                                                // İşçilər əvvəl (qazanca görə azalan), ofisiantlar həmişə sonda (onlar da qazanca görə)
                                                .sort((a, b) =>
                                                    ((a.type === 'waiter') - (b.type === 'waiter'))
                                                    || (Number(b.earned || 0) - Number(a.earned || 0))
                                                )
                                                .map(p => (
                                                <tr key={p.key}
                                                    onClick={() => setSelectedKey(p.key)}
                                                    className={`border-t border-slate-50 cursor-pointer transition-colors ${
                                                        p.key === selectedKey ? 'bg-primary-50' : 'hover:bg-slate-50'
                                                    }`}>
                                                    <td className="px-3 py-2.5 font-semibold text-sm text-slate-800">{p.name}</td>
                                                    <td className="px-3 py-2.5">
                                                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                                                            p.type === 'waiter' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                                        }`}>{p.type === 'waiter' ? '🧑‍🍳' : '👤'} {p.position}</span>
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right text-sm tabular-nums text-slate-700">{fmt(p.earned)}</td>
                                                    <td className="px-3 py-2.5 text-right text-sm tabular-nums text-green-600 font-semibold">{fmt(p.paid)}</td>
                                                    <td className={`px-3 py-2.5 text-right text-sm tabular-nums font-bold ${p.remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(p.remaining)}</td>
                                                    <td className="px-3 py-2.5 text-right text-[11px] text-slate-400">{p.last_payment || '—'}</td>
                                                    {isAdmin && (
                                                        <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                            {p.type === 'staff' ? (
                                                                <div className="flex items-center justify-end gap-1.5">
                                                                    <button onClick={() => openEditStaff(p)} title="Redaktə"
                                                                        className="rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100">✏️</button>
                                                                    <button onClick={() => deleteStaff(p)} title="Sil"
                                                                        className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-600 hover:bg-red-100">🗑</button>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center justify-end gap-1.5">
                                                                    <button onClick={() => openEditWaiter(p)} title="Ofisiantı redaktə et (ad + PIN)"
                                                                        className="rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100">✏️</button>
                                                                    <button onClick={() => deleteWaiter(p)} title="Ofisiantı sil"
                                                                        className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-600 hover:bg-red-100">🗑</button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                            {payroll.filter(p => staffTab === 'all' || p.type === staffTab).length === 0 && (
                                                <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400 text-sm">
                                                    {staffTab === 'waiter' ? 'Ofisiant yoxdur.' : staffTab === 'staff' ? 'İşçi yoxdur.' : 'Qeyd yoxdur.'}
                                                </td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Bu ayın maaş ödənişləri — refund (yalnız admin) ── */}
                {!loading && isAdmin && (
                    <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                        <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">
                            💵 Bu ayın maaş ödənişləri — refund
                        </h2>
                        {recentPays.length === 0 ? (
                            <p className="text-center text-sm text-slate-400 py-4">Bu ay ödəniş yoxdur</p>
                        ) : (
                            <div className="space-y-2">
                                {recentPays.map(p => (
                                    <div key={p.id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2.5">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold text-slate-800">
                                                {p.staff_name} — {Number(p.amount).toFixed(2)} ₼
                                            </div>
                                            <div className="text-[11px] text-slate-400 truncate">
                                                {p.created_at}{p.note ? ` · qeyd: ${p.note}` : ''}
                                            </div>
                                        </div>
                                        {Number(p.refunded) === 1 ? (
                                            <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-400">Refund edilib</span>
                                        ) : (
                                            <button
                                                onClick={() => refundPay(p)}
                                                disabled={refundingId === p.id}
                                                className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-100 disabled:opacity-50"
                                            >
                                                {refundingId === p.id ? '...' : '↩ Refund'}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Yeni işçi modal ── */}
            {showStaffForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
                    onClick={(e) => e.target === e.currentTarget && setShowStaffForm(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-4 text-lg">{editStaff ? 'İşçi redaktə' : 'Yeni işçi'}</h2>
                        <div className="grid gap-3">
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Ad Soyad</label>
                                <input type="text" value={staffForm.name}
                                    onChange={(e) => setStaffForm(f => ({ ...f, name: e.target.value }))}
                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-primary-400" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Vəzifə</label>
                                <select value={staffForm.position_id}
                                    onChange={(e) => setStaffForm(f => ({ ...f, position_id: e.target.value }))}
                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-primary-400">
                                    <option value="">— Seçin —</option>
                                    {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Maaş növü</label>
                                <div className="flex gap-2 mb-2">
                                    {[['fixed', 'Stabil maaş'], ['percent', 'Faiz maaşı']].map(([val, lbl]) => (
                                        <button key={val} type="button"
                                            onClick={() => setStaffForm(f => ({ ...f, salary_type: val }))}
                                            className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${staffForm.salary_type === val ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
                                            {lbl}
                                        </button>
                                    ))}
                                </div>
                                {staffForm.salary_type === 'percent' ? (
                                    <>
                                        <div className="relative">
                                            <input type="number" step="0.01" min="0" value={staffForm.salary_percent}
                                                onChange={(e) => setStaffForm(f => ({ ...f, salary_percent: e.target.value }))}
                                                placeholder="Faiz"
                                                className="w-full rounded-xl border border-slate-200 px-3 py-2 pr-7 text-sm font-mono focus:outline-none focus:border-primary-400" />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            {[['monthly', 'Aylıq'], ['live', 'Canlı']].map(([val, lbl]) => (
                                                <button key={val} type="button"
                                                    onClick={() => setStaffForm(f => ({ ...f, accrual_mode: val }))}
                                                    className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold border transition-all ${staffForm.accrual_mode === val ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
                                                    {lbl}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="text-[11px] text-slate-400 mt-1">
                                            {staffForm.accrual_mode === 'live'
                                                ? 'Canlı: balans hər sifarişdən sonra restoran satışının (nağd+POS, servis xaric) bu faizi qədər artır — istənilən vaxt ödəyə bilərsən.'
                                                : 'Aylıq: hər ayın 1-i keçən ayın restoran satışının (nağd+POS, servis xaric) bu faizi maaşa yatır.'}
                                        </p>
                                        <label className="text-xs font-semibold text-slate-500 mt-3 mb-1 block">Minimum əməkhaqqı (₼) — opsional</label>
                                        <input type="number" step="0.01" min="0" value={staffForm.salary_min}
                                            onChange={(e) => setStaffForm(f => ({ ...f, salary_min: e.target.value }))}
                                            placeholder="məs. 1500 (boş = minimum yoxdur)"
                                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary-400" />
                                        <p className="text-[11px] text-slate-400 mt-1">
                                            Aylıq zəmanət: faiz bu məbləğdən az gələrsə, həmin ay minimuma tamamlanır. (Götürdüklər nəzərə alınmır — yalnız gələn faizlər müqayisə olunur.)
                                        </p>
                                    </>
                                ) : (
                                    <input type="number" step="0.01" min="0" value={staffForm.salary}
                                        onChange={(e) => setStaffForm(f => ({ ...f, salary: e.target.value }))}
                                        placeholder="Aylıq maaş (₼)"
                                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary-400" />
                                )}
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Başlama tarixi</label>
                                <input type="date" value={staffForm.start_date}
                                    onChange={(e) => setStaffForm(f => ({ ...f, start_date: e.target.value }))}
                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-primary-400" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Bu ay götürülüb — kassasız (₼)</label>
                                <input type="number" step="0.01" min="0" value={staffForm.prepaid_this}
                                    onChange={(e) => setStaffForm(f => ({ ...f, prepaid_this: e.target.value }))}
                                    placeholder="məs. 50 (boş = 0)"
                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary-400" />
                                <p className="text-[11px] text-slate-400 mt-1">İşçi bu ay artıq başqa bölmədən (kassa onsuz da azalıb) məbləğ götürübsə bura yazın — bu ay qalığı həmin qədər azalar, kassaya təsir etməz. Gələn ay normal davam edir.</p>
                            </div>
                            {editStaff && (
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Köçmə — əvvəlki aylardan qalıq (₼)</label>
                                    <input type="number" step="0.01" value={staffForm.carried_over}
                                        onChange={(e) => setStaffForm(f => ({ ...f, carried_over: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary-400" />
                                    <p className="text-[11px] text-slate-400 mt-1">Qazanc = aylıq maaş + köçmə. Şişmiş qalığı düzəltmək üçün bu rəqəmi dəyişin (məs. 0 yazın).</p>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setShowStaffForm(false)}
                                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-500">Ləğv et</button>
                            <button onClick={saveStaff} disabled={saving}
                                className="flex-1 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-bold disabled:opacity-50">
                                {saving ? '...' : 'Saxla'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Cərimə modalı ── */}
            {showFine && selected && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
                    onClick={(e) => e.target === e.currentTarget && setShowFine(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-xs shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-1 text-lg">⚖️ Cərimə</h2>
                        <p className="text-xs text-slate-400 mb-1">{selected.name}</p>
                        <p className="text-[11px] text-slate-500 mb-3">Qalıq: <b>{fmt(selected.remaining)} ₼</b> — cərimə qalıqdan çıxılır.</p>
                        <input
                            type="number" step="0.01" min="0"
                            value={fineAmount}
                            onChange={(e) => setFineAmount(e.target.value)}
                            placeholder="Cərimə məbləği (₼)"
                            className="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-center text-2xl font-mono font-bold focus:outline-none focus:border-rose-400 mb-3"
                            autoFocus
                        />
                        <input
                            type="text"
                            value={fineNote}
                            onChange={(e) => setFineNote(e.target.value)}
                            placeholder="Səbəb (ixtiyari)"
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-rose-400 mb-4"
                        />
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setShowFine(false)}
                                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600">Ləğv et</button>
                            <button onClick={doFine} disabled={fining}
                                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold disabled:opacity-50">
                                {fining ? '...' : 'Cərimə yaz'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Yeni vəzifə modal ── */}
            {showPosForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
                    onClick={(e) => e.target === e.currentTarget && setShowPosForm(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-4 text-lg">Yeni vəzifə</h2>
                        <input type="text" value={posName}
                            onChange={(e) => setPosName(e.target.value)}
                            placeholder="məs: Aşbaz, Xadimə, Salatçı"
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-primary-400 mb-4" />
                        <div className="flex gap-2">
                            <button onClick={() => setShowPosForm(false)}
                                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-500">Ləğv et</button>
                            <button onClick={savePos} disabled={saving}
                                className="flex-1 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-bold disabled:opacity-50">
                                {saving ? '...' : 'Əlavə et'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Ofisiant redaktə popup (ad + PIN) ── */}
            {editWaiter && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                     onClick={(e) => e.target === e.currentTarget && setEditWaiter(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-xs p-5">
                        <h2 className="text-base font-bold text-slate-800 mb-1">✏️ Ofisiant redaktə</h2>
                        <p className="text-xs text-slate-400 mb-4">🧑‍🍳 {editWaiter.name}</p>
                        <form onSubmit={saveWaiterEdit} className="space-y-3">
                            <div>
                                <label className="text-[11px] font-semibold text-slate-500">Ad</label>
                                <input type="text" value={wName} onChange={e => setWName(e.target.value)}
                                    placeholder="Ofisiant adı" autoFocus
                                    className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold text-slate-500">PIN (mövcud — dəyişmək istəsən redaktə et)</label>
                                <input type="tel" inputMode="numeric" maxLength={4} value={wPin}
                                    onChange={e => setWPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                    placeholder="4 rəqəmli PIN"
                                    className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-center text-2xl font-mono tracking-widest placeholder:text-sm placeholder:tracking-normal focus:outline-none focus:border-blue-400" />
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setEditWaiter(null)}
                                    className="flex-1 border border-slate-200 rounded-xl py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Ləğv et</button>
                                <button type="submit" disabled={wSaving}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2 text-sm font-bold disabled:opacity-50">
                                    {wSaving ? '...' : 'Saxla'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}
